import { useState, useMemo } from 'react'
import { isAxiosError } from 'axios'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { useClinics } from '@/hooks/useClinics'
import { useViewMode } from '@/stores/viewMode'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/libs/api_client'
import { Users, Search, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { AllowedAction } from '@/data/allowedActions'

// Clinic 層級權限模組列表 (key 需與 Prisma ClinicUserPermissionModule enum 一致)
const CLINIC_PERMISSION_MODULES = [
  { key: 'clinic_user_management', label: '使用者管理' },
  { key: 'clinic_user_role', label: '角色管理' },
  { key: 'clinic_clinic_management', label: '診所管理' },
  { key: 'clinic_queue_system', label: '叫號系統' },
  { key: 'clinic_appointment_system', label: '預約系統' },
  { key: 'clinic_broadcast_device', label: '廣播設備' },
  { key: 'clinic_queue_machine_management', label: '叫號機管理' },
  { key: 'clinic_queue_machine_operation', label: '叫號機操作' },
]

const CRUD_ACTIONS = [
  { key: 'C', label: '新增' },
  { key: 'R', label: '讀取' },
  { key: 'U', label: '修改' },
  { key: 'D', label: '刪除' },
]

interface PermissionState {
  [module: string]: AllowedAction[]
}

interface CreateResult {
  clinic_id: number
  clinic_name: string
  user_role_id: number | null
  success: boolean
  error?: string
}

export default function CreateAllClinicUserRolePage() {
  const { mode, id: providerId } = useViewMode()
  const { token } = useAuth()

  // Form state
  const [roleName, setRoleName] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [permissions, setPermissions] = useState<PermissionState>({})
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  // UI state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [results, setResults] = useState<CreateResult[] | null>(null)

  // Fetch clinics
  const { clinics, isLoading: clinicsLoading } = useClinics({
    page: 1,
    pageSize: 1000,
    providerId: mode === 'provider' ? providerId ?? undefined : undefined,
  })

  // Filter clinics by search term
  const filteredClinics = useMemo(() => {
    if (!clinics) return []
    if (!searchTerm) return clinics
    const term = searchTerm.toLowerCase()
    return clinics.filter(
      (clinic) =>
        clinic.clinic_name.toLowerCase().includes(term) ||
        (clinic.alias && clinic.alias.toLowerCase().includes(term)) ||
        clinic.clinic_id.toString().includes(searchTerm),
    )
  }, [clinics, searchTerm])

  // Handle permission change
  const handlePermissionChange = (module: string, action: AllowedAction, checked: boolean) => {
    setPermissions((prev) => {
      const currentActions = prev[module] || []
      if (checked) {
        return { ...prev, [module]: [...currentActions, action] }
      } else {
        return { ...prev, [module]: currentActions.filter((a) => a !== action) }
      }
    })
  }

  // Handle clinic selection
  const handleClinicSelect = (clinicId: number, checked: boolean) => {
    if (checked) {
      setSelectedClinicIds((prev) => [...prev, clinicId])
    } else {
      setSelectedClinicIds((prev) => prev.filter((id) => id !== clinicId))
    }
  }

  // Handle select all clinics
  const handleSelectAllClinics = (checked: boolean) => {
    if (checked) {
      setSelectedClinicIds(filteredClinics.map((c) => c.clinic_id))
    } else {
      setSelectedClinicIds([])
    }
  }

  // Validate form
  const isFormValid = roleName.trim() !== '' && selectedClinicIds.length > 0

  // Convert permissions to API format
  const getPermissionsArray = () => {
    return Object.entries(permissions)
      .filter(([, actions]) => actions.length > 0)
      .map(([module, allowed_action]) => ({
        module,
        allowed_action,
      }))
  }

  // Submit handler
  const handleSubmit = async () => {
    setIsConfirmOpen(false)
    setIsSubmitting(true)
    setResults(null)

    try {
      const response = await apiClient.post(
        '/api/tools/create-all-clinic-user-role',
        {
          role_name: roleName.trim(),
          role_description: roleDescription.trim() || undefined,
          permissions: getPermissionsArray(),
          clinic_ids: selectedClinicIds,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      )

      const data = response.data
      if (data.success) {
        setResults(data.data.results)
        toast.success(
          `成功建立 ${data.data.created_count} 個角色，失敗 ${data.data.failed_count} 個`,
        )
      } else {
        toast.error(data.error?.message || '建立失敗')
      }
    } catch (error) {
      const message = isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message
        : undefined
      toast.error(message || '建立失敗')
    } finally {
      setIsSubmitting(false)
    }
  }

  const allClinicsSelected =
    filteredClinics.length > 0 &&
    filteredClinics.every((c) => selectedClinicIds.includes(c.clinic_id))

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="工具箱"
        title="建立所有診所角色"
        description="批量為多個診所建立相同的角色並設定權限"
        icon={<Users className="h-8 w-8" />}
        level="section"
      />

      {/* Results display */}
      {results && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>建立結果</CardTitle>
            <CardDescription>
              成功: {results.filter((r) => r.success).length} / 失敗:{' '}
              {results.filter((r) => !r.success).length}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {results.map((result) => (
                  <div
                    key={result.clinic_id}
                    className={`flex items-center gap-2 p-2 rounded ${
                      result.success ? 'bg-green-50' : 'bg-red-50'
                    }`}
                  >
                    {result.success ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className="font-medium">{result.clinic_name}</span>
                    {result.error && (
                      <span className="text-sm text-red-600">- {result.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            <Button className="mt-4" onClick={() => setResults(null)}>
              繼續建立
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main content */}
      {!results && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left side - Role settings */}
          <Card>
            <CardHeader>
              <CardTitle>角色設定</CardTitle>
              <CardDescription>設定角色名稱和權限</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Role name */}
              <div className="space-y-2">
                <Label htmlFor="roleName">角色名稱 *</Label>
                <Input
                  id="roleName"
                  placeholder="請輸入角色名稱"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
              </div>

              {/* Role description */}
              <div className="space-y-2">
                <Label htmlFor="roleDescription">角色說明</Label>
                <Input
                  id="roleDescription"
                  placeholder="請輸入角色說明（選填）"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                />
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <Label>權限設定</Label>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-4">
                    {CLINIC_PERMISSION_MODULES.map((module) => (
                      <div key={module.key} className="border rounded-lg p-4">
                        <div className="font-medium mb-3">{module.label}</div>
                        <div className="flex gap-4 flex-wrap">
                          {CRUD_ACTIONS.map((action) => (
                            <div key={action.key} className="flex items-center gap-2">
                              <Checkbox
                                id={`${module.key}-${action.key}`}
                                checked={(permissions[module.key] || []).includes(
                                  action.key as AllowedAction,
                                )}
                                onCheckedChange={(checked) =>
                                  handlePermissionChange(
                                    module.key,
                                    action.key as AllowedAction,
                                    checked as boolean,
                                  )
                                }
                              />
                              <Label
                                htmlFor={`${module.key}-${action.key}`}
                                className="text-sm cursor-pointer"
                              >
                                {action.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>

          {/* Right side - Clinic selection */}
          <Card>
            <CardHeader>
              <CardTitle>選擇診所</CardTitle>
              <CardDescription>
                已選擇 {selectedClinicIds.length} 個診所
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜尋診所名稱..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Select all */}
              <div className="flex items-center gap-2 border-b pb-2">
                <Checkbox
                  id="select-all"
                  checked={allClinicsSelected}
                  onCheckedChange={handleSelectAllClinics}
                />
                <Label htmlFor="select-all" className="cursor-pointer font-medium">
                  全選
                </Label>
              </div>

              {/* Clinic list */}
              {clinicsLoading ? (
                <div className="flex items-center justify-center h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2 pr-4">
                    {filteredClinics.map((clinic) => (
                      <div
                        key={clinic.clinic_id}
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={`clinic-${clinic.clinic_id}`}
                          checked={selectedClinicIds.includes(clinic.clinic_id)}
                          onCheckedChange={(checked) =>
                            handleClinicSelect(clinic.clinic_id, checked as boolean)
                          }
                        />
                        <Label
                          htmlFor={`clinic-${clinic.clinic_id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="font-medium">{clinic.clinic_name}</div>
                          {clinic.alias && (
                            <div className="text-sm text-muted-foreground">{clinic.alias}</div>
                          )}
                        </Label>
                      </div>
                    ))}
                    {filteredClinics.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        沒有找到診所
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer with save button */}
      {!results && (
        <div className="flex justify-end mt-6">
          <Button
            size="lg"
            disabled={!isFormValid || isSubmitting}
            onClick={() => setIsConfirmOpen(true)}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            儲存
          </Button>
        </div>
      )}

      {/* Confirmation dialog */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認建立角色</AlertDialogTitle>
            <AlertDialogDescription>
              即將為 {selectedClinicIds.length} 個診所建立角色「{roleName}」。此操作無法復原，確定要繼續嗎？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit}>確認建立</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
